import {
	AddCircle as AddCircleIcon,
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	Cancel as CancelIcon,
	CheckBox as CheckBoxIcon,
	Close as CloseIcon,
	DeleteOutline as DeleteOutlineIcon,
	DirectionsCarFilled as DirectionsCarFilledIcon,
	Download as DownloadIcon,
	FileUpload as FileUploadIcon,
	PeopleAlt as PeopleAltIcon,
	Search as SearchIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, IconButton, InputAdornment, Menu, MenuItem, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPagination, GridPaginationModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { ChangeEvent, FC, FormEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ConfirmationDialog from "../../../components/ConfirmationDialog";
import NewUploadDataDialog from "../../../components/NewUploadDataDialog";
import SecureLoginAccessButton from "../../../components/SecureLoginAccessButton";
import TablePagination from "../../../components/TablePagination";
import { useDownloadJob } from "../../../hooks/useDownloadReport";
import { useAppDispatch } from "../../../redux";
import {
	useDeleteBlacklistVehiclesMutation,
	useGetBlacklistedVehicleCountsQuery,
	useGetBlacklistedVehiclesListQuery,
	useUploadBlacklistVehiclesDataMutation
} from "../../../redux/reducers/dataManagement.reducer";
import { showFeedbackNotification } from "../../../redux/reducers/feedbackNotification.reducer";
import { openSecureLoginDialog } from "../../../redux/reducers/secureLoginDialog.reducer";
import { useGetVehicleTypesQuery } from "../../../redux/reducers/settings.reducer";
import {
	CombinedRowType,
	IBlacklistedVehicleDetails,
	IGetBlacklistedVehiclesListRequestData,
	IUploadBlacklistVehicleExcelDetails,
	IUploadBlacklistVehiclesExcelResponseData,
	IUploadResponse,
	IUploadResponseBlacklistDetails,
	IUploadResponseData
} from "../../../types";
import { PAGINATION_CONSTANTS, VEHICLE_TYPE_E } from "../../../utils/constants";
import AddBlacklistVehicleDialog from "./AddBlacklistVehicleDialog";
import "./blacklist.css";

interface IBlacklistVehicleDetails {
	id: number;
	sr_no: number;
	vehicle_number: string;
	vehicle_type: number;
	reason: string;
}

const BlacklistPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const Blacklist: FC = () => {
	const dispatch = useAppDispatch();

	const { isLoading: showDownloadButtonLoader, startDownload } = useDownloadJob();

	const searchInputRef = useRef<HTMLInputElement>(null);

	const [searchInput, setSearchInput] = useState<string>("");
	const [showAddBlacklistVehicleDialog, setShowAddBlacklistVehicleDialog] = useState<boolean>(false);
	const [showUploadDataDialog, setShowUploadDataDialog] = useState<boolean>(false);
	const [rowSelectionEnabled, setRowSelectionEnabled] = useState<boolean>(false);
	const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
	const [uploadDataLoading, setUploadDataLoading] = useState<boolean>(false);

	const [filters, setFilters] = useState<IGetBlacklistedVehiclesListRequestData>({
		auth_type: "blacklist",
		page: 1
	});

	// TABLE RENDERER STATES
	const [vehicleTypeDropdownAnchorElement, setVehicleTypeDropdownAnchorElement] = useState<HTMLElement | null>(null);
	const openVehicleTypeDropdown = Boolean(vehicleTypeDropdownAnchorElement);

	// APIS
	const {
		data: getBlacklistedVehiclesResponse,
		isLoading: getBlacklistedVehiclesLoading,
		isFetching: getBlacklistedVehiclesFetching,
		refetch: refetchBlacklistedVehicles
	} = useGetBlacklistedVehiclesListQuery(filters);

	const { data: blacklistedVehicleCounts, refetch: refetchBlacklistedVehicleCounts } =
		useGetBlacklistedVehicleCountsQuery();

	const [
		deleteBlacklistVehicles,
		{
			isLoading: deleteBlacklistVehiclesLoading,
			isSuccess: deleteBlacklistVehiclesSuccess,
			error: deleteBlacklistVehiclesError,
			reset: resetDeleteBlacklistVehiclesState
		}
	] = useDeleteBlacklistVehiclesMutation();

	const [uploadBlacklistVehiclesData] = useUploadBlacklistVehiclesDataMutation();

	// GET VEHICLE TYPES
	const { data: getVehicleTypesResponse } = useGetVehicleTypesQuery();

	const vehicleTypesList = useMemo(() => getVehicleTypesResponse ?? [], [getVehicleTypesResponse]);

	const refetchAllData = useCallback(() => {
		refetchBlacklistedVehicles();
		refetchBlacklistedVehicleCounts();
	}, [refetchBlacklistedVehicleCounts, refetchBlacklistedVehicles]);

	function handleChangeSearchInput(event: ChangeEvent<HTMLInputElement>) {
		setSearchInput(event.target.value);
	}

	function handleClearSearchInput() {
		setSearchInput("");
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };
			delete updatedFilters.number_plate;
			updatedFilters.page = 1;
			return updatedFilters;
		});

		if (searchInputRef.current) searchInputRef.current.focus();
	}

	function handleOpenAddBlacklistVehicleDialog() {
		setShowAddBlacklistVehicleDialog(true);
	}

	function handleCloseAddBlacklistVehicleDialog() {
		setShowAddBlacklistVehicleDialog(false);
		refetchAllData();
	}

	function handleOpenUploadDataDialog() {
		setShowUploadDataDialog(true);
	}

	function handleCloseUploadDataDialog() {
		setShowUploadDataDialog(false);
		refetchAllData();
	}

	function handleUploadBlacklistDetails(
		rowsData: CombinedRowType[],
		_customFlatSelectionEnabled?: boolean,
		_devices?: number[],
		isVerified?: boolean | undefined
	): Promise<IUploadResponse> {
		return new Promise((resolve) => {
			setUploadDataLoading(true);

			const uploadData: IUploadBlacklistVehicleExcelDetails[] = [];

			for (const rowDetails of rowsData) {
				if (rowDetails.is_deleted) continue;

				const vehicleTypeDetails = vehicleTypesList.find(
					(vehicleTypeItem) => vehicleTypeItem.name === rowDetails["Vehicle Type"]
				);

				const uploadDataItem: IUploadBlacklistVehicleExcelDetails = {
					number_plate: rowDetails["Number Plate"],
					vehicle_type: vehicleTypeDetails?.id ?? VEHICLE_TYPE_E.FOUR_WHEELER,
					auth_type: "blacklist",
					comment: rowDetails.Comment
				};

				uploadData.push(uploadDataItem);
			}

			if (uploadData.length <= 0) {
				resolve({
					data: [],
					message: ""
				});
				return;
			}

			uploadBlacklistVehiclesData({
				data: uploadData,
				is_verified: isVerified ?? false
			})
				.unwrap()
				.then((response) => {
					resolve({
						data: [],
						message: response.message
					});

					dispatch(
						showFeedbackNotification({
							message: response.message,
							severity: "success"
						})
					);
				})
				.catch((error) => {
					if (!error.data) {
						resolve({
							data: [],
							message: "Something went wrong"
						});
						return;
					}

					const errorData = error.data as IUploadBlacklistVehiclesExcelResponseData;

					dispatch(
						showFeedbackNotification({
							message: errorData.message,
							severity: "error"
						})
					);

					const filteredErrorData = errorData.data.filter(
						(item) => !!item.database_data && Object.keys(item.database_data).length > 0
					);

					const returnData: IUploadResponseBlacklistDetails[] = [];

					for (const errorDataItem of filteredErrorData) {
						if (!errorDataItem.remarks) continue;

						if (errorDataItem.remarks === "Vehicle already present") {
							const vehicleTypeDetails = vehicleTypesList.find(
								(vehicleTypeItem) => vehicleTypeItem.id === errorDataItem.database_data.vehicle?.vehicle_type || -1
							);

							returnData.push({
								remarks: errorDataItem.remarks,
								"Number Plate": errorDataItem.database_data.vehicle?.number_plate ?? "",
								"Vehicle Type": vehicleTypeDetails?.name ?? "",
								Comment: errorDataItem.database_data.vehicle?.comment ?? "",
								id: 0,
								sr_no: 0,
								is_deleted: false,
								errorFields:
									errorDataItem.database_data.vehicle && errorDataItem.database_data.vehicle.auth_type === "whitelist"
										? {
												"Number Plate": ["This number plate already exists in whitelist"]
										  }
										: {
												"Number Plate": ["Vehicle already present"]
										  }
							});
						}
					}

					resolve({
						data: returnData as IUploadResponseData[],
						message: errorData.message
					});
				})
				.finally(() => {
					setUploadDataLoading(false);
				});
		});
	}

	function handleSearchBlacklistVehicle(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();

		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (searchInput) updatedFilters.number_plate = searchInput;
			else delete updatedFilters.number_plate;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function handleDownloadButtonClick() {
		startDownload({ report_type: "blacklist_vehicles" });
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function handleEnableRowSelection() {
		setRowSelectionEnabled(true);
	}

	function handleDisableRowSelection() {
		setRowSelectionEnabled(false);
		setSelectedRowIds([]);
	}

	function handleSelectRows(updatedValues: GridRowSelectionModel) {
		setSelectedRowIds(updatedValues as number[]);
	}

	function handleOpenDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(true);
	}

	function handleCloseDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
	}

	function handleDeleteSelectedVehicles() {
		deleteBlacklistVehicles(selectedRowIds);
	}

	function handleOpenSecureLoginDialog() {
		dispatch(openSecureLoginDialog());
	}

	useEffect(() => {
		if (deleteBlacklistVehiclesSuccess) {
			resetDeleteBlacklistVehiclesState();
			handleCloseDeleteConfirmationDialog();
			handleDisableRowSelection();
			refetchAllData();
		}
	}, [deleteBlacklistVehiclesSuccess, refetchAllData, resetDeleteBlacklistVehiclesState]);

	useEffect(() => {
		if (deleteBlacklistVehiclesError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in deleteBlacklistVehiclesError && deleteBlacklistVehiclesError.data
							? String(deleteBlacklistVehiclesError)
							: "Failed to delete blacklisted vehicles",
					severity: "error"
				})
			);

			resetDeleteBlacklistVehiclesState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteBlacklistVehiclesError]);

	// TABLE RENDERER FUNCTIONS
	function handleOpenVehicleTypeDropdown(event: MouseEvent<HTMLDivElement>) {
		setVehicleTypeDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseVehicleTypeDropdown() {
		setVehicleTypeDropdownAnchorElement(null);
	}

	function handleChangeSelectedVehicleType(updatedValue: number) {
		handleCloseVehicleTypeDropdown();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (updatedValue >= 0) updatedFilters.vehicle_type = updatedValue;
			else delete updatedFilters.vehicle_type;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function renderVehicleTypeDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper center"
					id="vehicle-type-dropdown"
					aria-controls={openVehicleTypeDropdown ? "vehicle-type-dropdown-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={openVehicleTypeDropdown ? "true" : undefined}
					onClick={handleOpenVehicleTypeDropdown}
				>
					<Typography variant="subtitle2" textTransform="capitalize">
						{typeof filters.vehicle_type === "number"
							? vehicleTypesList.find((vehicleTypeItem) => vehicleTypeItem.id === filters.vehicle_type)?.name ??
							  "Vehicle Type"
							: "Vehicle Type"}
					</Typography>
					{openVehicleTypeDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="vehicle-type-dropdown-menu"
					anchorEl={vehicleTypeDropdownAnchorElement}
					open={openVehicleTypeDropdown}
					onClose={handleCloseVehicleTypeDropdown}
					MenuListProps={{ "aria-labelledby": "vehicle-type-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedVehicleType(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{vehicleTypesList.map((vehicleTypeItem) => (
						<MenuItem key={vehicleTypeItem.id} onClick={() => handleChangeSelectedVehicleType(vehicleTypeItem.id)}>
							{vehicleTypeItem.name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 100,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "vehicle_number",
			headerName: "Vehicle Number",
			flex: 1,
			minWidth: 150,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "vehicle_type",
			headerName: "Vehicle Type",
			flex: 1,
			minWidth: 150,
			sortable: false,
			align: "center",
			renderHeader: renderVehicleTypeDropdown,
			// valueGetter: (params) => (params.value === 0 ? "Four Wheeler" : "Two Wheeler")
			valueGetter: (params) =>
				vehicleTypesList.find((vehicleTypeItem) => vehicleTypeItem.id === params.value)?.name ?? ""
		},
		{
			field: "reason",
			headerName: "Reason",
			flex: 2,
			minWidth: 200,
			sortable: false,
			align: "center",
			headerAlign: "center"
		}
	];

	function getBlacklistRowDetails(blacklist: IBlacklistedVehicleDetails[]): IBlacklistVehicleDetails[] {
		const activePage = filters.page - 1;

		return blacklist.map((blacklistVehicleItem, index) => ({
			id: blacklistVehicleItem.id,
			sr_no: activePage * PAGINATION_CONSTANTS.ROWS_PER_PAGE + index + 1,
			vehicle_number: blacklistVehicleItem.number_plate,
			vehicle_type: blacklistVehicleItem.vehicle_type,
			reason: blacklistVehicleItem.comment ?? ""
		}));
	}

	return (
		<Box className="blacklist-screen-wrapper">
			<Grid container spacing={2}>
				<Grid item xs={6} xl={3}>
					<Box className="blacklist-data-card">
						<Box className="blacklist-data-card-icon">
							<DirectionsCarFilledIcon color="error" sx={{ fontSize: "28px" }} />
						</Box>

						<Box className="blacklist-data-card-details">
							<Typography variant="body2" color="#00000080">
								Blacklisted Vehicles
							</Typography>

							<Typography fontWeight={600}>{blacklistedVehicleCounts?.black_listed_vehicles ?? 0}</Typography>
						</Box>
					</Box>
				</Grid>

				<Grid item xs={6} xl={3}>
					<Box className="blacklist-data-card">
						<Box className="blacklist-data-card-icon">
							<PeopleAltIcon color="error" sx={{ fontSize: "28px" }} />
						</Box>

						<Box className="blacklist-data-card-details">
							<Typography variant="body2" color="#00000080">
								Blacklisted People
							</Typography>

							<Typography fontWeight={600}>{blacklistedVehicleCounts?.black_listed_people ?? 0}</Typography>
						</Box>
					</Box>
				</Grid>
			</Grid>

			<Box className="blacklist-wrapper">
				{/* Mobile Header Layout */}
				<Box className="blacklist-header-wrapper mobile-header">
					{/* 1st Row: Search Input (Full Width) */}
					<Box
						className="blacklist-header-actions-wrapper"
						component="form"
						noValidate
						onSubmit={handleSearchBlacklistVehicle}
					>
						<TextField
							fullWidth
							variant="filled"
							placeholder="Search"
							size="small"
							value={searchInput}
							onChange={handleChangeSearchInput}
							InputProps={{
								disableUnderline: true,
								hiddenLabel: true,
								classes: { root: "filled-input-root event-logs-search-input" },
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" size="small" type="submit" color="primary">
											<SearchIcon fontSize="medium" />
										</IconButton>
									</InputAdornment>
								),
								endAdornment: filters.number_plate ? (
									<InputAdornment position="end">
										<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									</InputAdornment>
								) : null
							}}
						/>
					</Box>

					{/* 2nd Row: Upload & Download Buttons (Equal Widths) */}
					<Box className="blacklist-header-actions-wrapper">
						<SecureLoginAccessButton
							variant="outlined"
							color="primary"
							startIcon={<FileUploadIcon />}
							sx={{ flex: 1 }}
							onClick={handleOpenUploadDataDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Upload
						</SecureLoginAccessButton>

						<LoadingButton
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={showDownloadButtonLoader}
							loadingPosition="start"
							sx={{ flex: 1 }}
						>
							Download
						</LoadingButton>
					</Box>

					{/* 3rd Row: Add, Select/Cancel, Remove Buttons (Equal Widths) */}
					<Box className="blacklist-header-actions-wrapper">
						<SecureLoginAccessButton
							variant="outlined"
							color="success"
							startIcon={<AddCircleIcon />}
							sx={{ flex: 1 }}
							onClick={handleOpenAddBlacklistVehicleDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Add
						</SecureLoginAccessButton>

						{rowSelectionEnabled ? (
							<SecureLoginAccessButton
								variant="outlined"
								color="primary"
								startIcon={<CancelIcon />}
								sx={{ flex: 1 }}
								onClick={handleDisableRowSelection}
								tooltipAction={handleOpenSecureLoginDialog}
							>
								Cancel
							</SecureLoginAccessButton>
						) : (
							<SecureLoginAccessButton
								variant="outlined"
								color="primary"
								startIcon={<CheckBoxIcon />}
								sx={{ flex: 1 }}
								onClick={handleEnableRowSelection}
								tooltipAction={handleOpenSecureLoginDialog}
							>
								Select
							</SecureLoginAccessButton>
						)}

						<SecureLoginAccessButton
							variant="outlined"
							color="error"
							startIcon={<DeleteOutlineIcon />}
							sx={{ flex: 1 }}
							disabled={selectedRowIds.length <= 0}
							onClick={handleOpenDeleteConfirmationDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Remove
						</SecureLoginAccessButton>
					</Box>
				</Box>

				{/* Desktop Header Layout */}
				<Box className="blacklist-header-wrapper desktop-header">
					<Box className="blacklist-header-actions-wrapper">
						<SecureLoginAccessButton
							variant="outlined"
							color="success"
							startIcon={<AddCircleIcon />}
							sx={{ paddingX: 2 }}
							onClick={handleOpenAddBlacklistVehicleDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Add
						</SecureLoginAccessButton>

						{rowSelectionEnabled ? (
							<SecureLoginAccessButton
								variant="outlined"
								color="primary"
								startIcon={<CancelIcon />}
								sx={{ paddingX: 2 }}
								onClick={handleDisableRowSelection}
								tooltipAction={handleOpenSecureLoginDialog}
							>
								Cancel
							</SecureLoginAccessButton>
						) : (
							<SecureLoginAccessButton
								variant="outlined"
								color="primary"
								startIcon={<CheckBoxIcon />}
								sx={{ paddingX: 2 }}
								onClick={handleEnableRowSelection}
								tooltipAction={handleOpenSecureLoginDialog}
							>
								Select
							</SecureLoginAccessButton>
						)}

						<SecureLoginAccessButton
							variant="outlined"
							color="error"
							startIcon={<DeleteOutlineIcon />}
							sx={{ paddingX: 2 }}
							disabled={selectedRowIds.length <= 0}
							onClick={handleOpenDeleteConfirmationDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Remove
						</SecureLoginAccessButton>
					</Box>

					<Box className="blacklist-header-actions-wrapper">
						<Box component="form" noValidate onSubmit={handleSearchBlacklistVehicle}>
							<TextField
								fullWidth
								variant="filled"
								placeholder="Search"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root event-logs-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.number_plate ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>

						<SecureLoginAccessButton
							variant="outlined"
							color="primary"
							startIcon={<FileUploadIcon />}
							sx={{ paddingX: 3 }}
							onClick={handleOpenUploadDataDialog}
							tooltipAction={handleOpenSecureLoginDialog}
						>
							Upload
						</SecureLoginAccessButton>

						<LoadingButton
							disableElevation
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadButtonClick}
							loading={showDownloadButtonLoader}
							loadingPosition="start"
						>
							Download
						</LoadingButton>
					</Box>
				</Box>

				<Box className="blacklist-table-wrapper">
					<DataGrid
						columns={columnsData}
						rows={getBlacklistRowDetails(getBlacklistedVehiclesResponse?.results ?? [])}
						getRowId={(item) => item.id}
						rowSelectionModel={selectedRowIds}
						checkboxSelection={rowSelectionEnabled}
						onRowSelectionModelChange={handleSelectRows}
						loading={getBlacklistedVehiclesLoading || getBlacklistedVehiclesFetching}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={60}
						paginationMode="server"
						pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
						onPaginationModelChange={handlePageChange}
						rowCount={getBlacklistedVehiclesResponse?.count ?? 0}
						paginationModel={{ page: filters.page - 1, pageSize: PAGINATION_CONSTANTS.ROWS_PER_PAGE }}
						slots={{ pagination: BlacklistPagination }}
						slotProps={{ footer: { sx: { justifyContent: "center" } } }}
						hideFooter={(getBlacklistedVehiclesResponse?.count ?? 0) <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
						classes={{
							root: "blacklist-table-root",
							columnSeparator: "blacklist-table-column-separator",
							columnHeader: "blacklist-table-column-headers",
							cell: "blacklist-table-cell"
						}}
					/>
				</Box>
			</Box>

			<AddBlacklistVehicleDialog open={showAddBlacklistVehicleDialog} onClose={handleCloseAddBlacklistVehicleDialog} />

			{/* <UploadDataDialog
				open={showUploadDataDialog}
				onClose={handleCloseUploadDataDialog}
				onUploadButtonClick={handleUploadBlacklistDetails}
				sampleFileUrl={"/analytics/blacklist_vehicles/download_template"}
				sampleFileName={"BlacklistSample.xlsx"}
				// uploadButtonLoading={uploadBlacklistDataLoading}
			/> */}

			<NewUploadDataDialog
				open={showUploadDataDialog}
				sampleFileUrl="/analytics/blacklist_vehicles/download_template"
				onClose={handleCloseUploadDataDialog}
				onUploadButtonClick={handleUploadBlacklistDetails}
				uploadButtonLoading={uploadDataLoading}
				disableCustomFlatNumber
			/>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				title="Delete Vehicles"
				heading="Are you sure you want to delete selected vehicles?"
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteSelectedVehicles}
				color="error"
				confirmButtonLoading={deleteBlacklistVehiclesLoading}
				width="sm"
			/>
		</Box>
	);
};

export default Blacklist;
